"use client";

import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import {
  Plane,
  Building2,
  Globe2,
  MapPin,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";
import {
  CARGO_KPI_NAMES,
  fetchKPIs,
} from "@/lib/api/datosGenerales/fetchKPIs";
import type { KPIs } from "@/lib/api/datosGenerales/types/kpis";
import { intlTag, type Locale } from "@/i18n/config";

/** Los cuatro conteos que acompañan a la cifra principal, en orden de lectura. */
const COUNT_KPIS = [
  { name: "flight_cargo", icon: Plane },
  { name: "distinct_airline_cargo", icon: Building2 },
  { name: "distinct_airport_cargo", icon: MapPin },
  { name: "distinct_country_cargo", icon: Globe2 },
] as const;

/**
 * Pareja categórica de la barra de composición. No son los hex de marca tal
 * cual: el azul #102542 se sale de la banda de luminosidad para marcas de datos
 * y el dorado #ffb800 no llega a 3:1 contra el blanco. Estos son el escalón más
 * cercano de cada familia que sí pasa (banda, croma, separación CVD y
 * contraste), así que la barra sigue leyéndose como marca y además es legible
 * con daltonismo.
 */
const COLOR_FREIGHT = "#2f6fb5";
const COLOR_BELLY = "#c38a00";

type Variation = { value: number; direction: "up" | "down" | "flat" };

/** `variation_pct` viene como fracción (0.0324 = +3.24 %), no como porcentaje. */
function variation(kpi: KPIs | undefined): Variation {
  const value = (kpi?.variation_pct ?? 0) * 100;
  if (Math.abs(value) < 0.05) return { value: 0, direction: "flat" };
  return { value, direction: value > 0 ? "up" : "down" };
}

export default function CargaKPIs() {
  const locale = useLocale() as Locale;
  const t = useTranslations("carga.resultados");
  const [kpis, setKpis] = useState<KPIs[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetchKPIs(CARGO_KPI_NAMES)
      .then((items) => {
        if (cancelled) return;
        // Un 200 sin ninguno de los KPIs de carga pintaría la sección entera en
        // ceros, que se lee como un dato real. Mejor no enseñarla.
        if (items.length === 0) setError(true);
        else setKpis(items);
      })
      .catch((err) => {
        console.error("Error al cargar KPIs de carga:", err);
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const find = (name: string) =>
    kpis?.find((k) => k.kpi_name.toLowerCase() === name.toLowerCase());

  const total = find("cargo_all_tm");
  const freight = find("cargo_freight_tm");
  const belly = find("cargo_belly_tm");

  const nf = (value: number, decimals = 0) =>
    value.toLocaleString(intlTag(locale), {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

  /** "2026-07-01" -> "Julio 2026", en el idioma activo. */
  const periodo = (() => {
    const eventDate = total?.event_date ?? kpis?.[0]?.event_date;
    if (!eventDate) return null;
    const [year, month] = eventDate.split("-");
    const date = new Date(Number(year), Number(month) - 1, 1);
    const nombre = date.toLocaleString(intlTag(locale), { month: "long" });
    return `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} ${year}`;
  })();

  // Reparto cargueros/belly. Se calcula sobre la suma de las dos partes y no
  // sobre `cargo_all_tm` para que los dos segmentos siempre cierren en 100 %
  // aunque el total llegue redondeado distinto.
  const freightTm = freight?.kpi_value ?? 0;
  const bellyTm = belly?.kpi_value ?? 0;
  const partes = freightTm + bellyTm;
  const freightPct = partes > 0 ? (freightTm / partes) * 100 : 0;
  const bellyPct = partes > 0 ? (bellyTm / partes) * 100 : 0;

  if (error) return null;

  return (
    <section className="bg-editorial">
      <div className="mx-auto w-[90%] max-w-6xl py-16 md:py-24">
        <div className="mb-10 flex flex-col gap-6 md:mb-12 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <span className="mb-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#102542]">
              <span className="h-px w-8 bg-[#ffb800]" />
              {t("eyebrow")}
            </span>
            <h2 className="text-3xl font-bold tracking-tight text-[#102542] md:text-4xl">
              {t("title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-gray-600">
              {t("desc")}
            </p>
          </div>

          {periodo && (
            <span className="inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-gray-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#102542] md:self-auto">
              <span className="h-1.5 w-1.5 rounded-full bg-[#ffb800]" />
              {periodo}
            </span>
          )}
        </div>

        {kpis === null ? (
          <KPISkeleton />
        ) : (
          <>
            {/* Cifra principal + reparto cargueros / belly */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 md:p-10">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("kpis.cargo_all_tm")}
                  </p>
                  <p className="mt-2 text-5xl font-semibold tracking-tight text-[#102542] md:text-6xl">
                    {nf(total?.kpi_value ?? 0)}
                    <span className="ml-2 align-baseline text-xl font-medium text-gray-400 md:text-2xl">
                      {t("unidad")}
                    </span>
                  </p>
                </div>
                <Delta {...variation(total)} label={t("variacion")} />
              </div>

              <div className="mt-8 md:mt-10">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">
                  {t("composicion.title")}
                </p>

                {/* Parte-todo de dos segmentos: barra apilada horizontal, con
                    2px de superficie entre los tramos y etiqueta directa
                    debajo, no una dona de dos porciones. */}
                <div
                  role="img"
                  aria-label={`${t("composicion.freight")}: ${nf(freightTm)} ${t("unidad")} (${nf(freightPct, 0)}%). ${t("composicion.belly")}: ${nf(bellyTm)} ${t("unidad")} (${nf(bellyPct, 0)}%).`}
                  className="flex h-3 w-full gap-[2px] overflow-hidden"
                >
                  <span
                    className="h-full rounded-l-[4px]"
                    style={{
                      width: `${freightPct}%`,
                      backgroundColor: COLOR_FREIGHT,
                    }}
                    title={`${t("composicion.freight")} · ${nf(freightTm)} ${t("unidad")}`}
                  />
                  <span
                    className="h-full rounded-r-[4px]"
                    style={{
                      width: `${bellyPct}%`,
                      backgroundColor: COLOR_BELLY,
                    }}
                    title={`${t("composicion.belly")} · ${nf(bellyTm)} ${t("unidad")}`}
                  />
                </div>

                <dl className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <LeyendaItem
                    color={COLOR_FREIGHT}
                    label={t("composicion.freight")}
                    valor={`${nf(freightTm)} ${t("unidad")}`}
                    pct={`${nf(freightPct, 0)}%`}
                    variacion={variation(freight)}
                  />
                  <LeyendaItem
                    color={COLOR_BELLY}
                    label={t("composicion.belly")}
                    valor={`${nf(bellyTm)} ${t("unidad")}`}
                    pct={`${nf(bellyPct, 0)}%`}
                    variacion={variation(belly)}
                  />
                </dl>
              </div>
            </div>

            {/* Conteos de la red de carga */}
            <div className="mt-6 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-gray-200 bg-gray-200 sm:grid-cols-2 lg:grid-cols-4">
              {COUNT_KPIS.map(({ name, icon: Icon }) => {
                const kpi = find(name);
                return (
                  <article
                    key={name}
                    className="flex min-h-[170px] flex-col justify-between bg-white p-6 transition-colors hover:bg-gray-50 md:p-8"
                  >
                    <div className="mb-8 flex items-start justify-between">
                      <Icon className="h-6 w-6 text-[#102542]" />
                      <Delta {...variation(kpi)} compact />
                    </div>
                    <div>
                      <p className="text-3xl font-semibold tracking-tight text-[#102542]">
                        {nf(kpi?.kpi_value ?? 0)}
                      </p>
                      <p className="mt-1.5 text-sm leading-relaxed text-gray-500">
                        {t(`kpis.${name}`)}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>

            <p className="mt-6 text-xs text-gray-500">{t("fuente")}</p>
          </>
        )}
      </div>
    </section>
  );
}

function LeyendaItem({
  color,
  label,
  valor,
  pct,
  variacion,
}: {
  color: string;
  label: string;
  valor: string;
  pct: string;
  variacion: Variation;
}) {
  return (
    <div className="flex items-start gap-3">
      <span
        aria-hidden
        className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <div className="min-w-0">
        <dt className="text-sm text-gray-600">{label}</dt>
        <dd className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-base font-semibold tabular-nums text-[#102542]">
            {valor}
          </span>
          <span className="text-sm tabular-nums text-gray-400">{pct}</span>
          <Delta {...variacion} compact />
        </dd>
      </div>
    </div>
  );
}

/**
 * Variación contra el periodo anterior. El signo y la flecha van siempre, así
 * que el dato no depende sólo del color.
 */
function Delta({
  value,
  direction,
  label,
  compact = false,
}: Variation & { label?: string; compact?: boolean }) {
  const Icon =
    direction === "up" ? TrendingUp : direction === "down" ? TrendingDown : Minus;
  const tone =
    direction === "up"
      ? "text-green-700"
      : direction === "down"
        ? "text-red-700"
        : "text-gray-500";
  const signo = direction === "up" ? "+" : "";

  return (
    <span
      className={`inline-flex items-center gap-1.5 ${compact ? "text-xs" : "text-sm"} font-semibold ${tone}`}
    >
      <Icon className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} aria-hidden />
      <span className="tabular-nums">
        {signo}
        {value.toFixed(1)}%
      </span>
      {label && (
        <span className="font-normal text-gray-500">{label}</span>
      )}
    </span>
  );
}

function KPISkeleton() {
  return (
    <div aria-hidden className="animate-pulse">
      <div className="rounded-2xl border border-gray-200 bg-white p-6 md:p-10">
        <div className="h-4 w-48 rounded bg-gray-200" />
        <div className="mt-3 h-12 w-64 rounded bg-gray-200 md:h-14" />
        <div className="mt-10 h-3 w-full rounded bg-gray-200" />
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="h-10 rounded bg-gray-100" />
          <div className="h-10 rounded bg-gray-100" />
        </div>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-gray-200 bg-gray-200 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="min-h-[170px] bg-white p-6 md:p-8">
            <div className="h-6 w-6 rounded bg-gray-200" />
            <div className="mt-12 h-8 w-24 rounded bg-gray-200" />
            <div className="mt-2 h-4 w-32 rounded bg-gray-100" />
          </div>
        ))}
      </div>
    </div>
  );
}
